<template>
  <div class="reset-container">
    <!-- 背景图模糊层 -->
    <div class="bg-layer"></div>

    <!-- 重置密码卡片 -->
    <div class="reset-card">
      <!-- 返回登录 -->
      <button type="button" class="back-btn" @click="handleBack">
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path
            d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
            fill="currentColor"
          />
        </svg>
        返回登录
      </button>

      <!-- 标题区域 -->
      <div class="reset-header">
        <div class="logo-icon">
          <svg viewBox="0 0 24 24" width="36" height="36">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
              fill="currentColor"
            />
          </svg>
        </div>
        <h1 class="title">找回密码</h1>
        <p class="subtitle" v-if="step === 1">验证身份，重置您的账号密码</p>
        <p class="subtitle" v-else>设置新密码，确保账号安全</p>
        <!-- 步骤指示器 -->
        <div class="step-indicator">
          <div
            class="step-dot"
            :class="{ active: step === 1, done: step === 2 }"
          >
            <span v-if="step === 1">1</span>
            <span v-else>✓</span>
          </div>
          <div class="step-line" :class="{ active: step === 2 }"></div>
          <div class="step-dot" :class="{ active: step === 2 }">
            <span>2</span>
          </div>
        </div>
      </div>

      <!-- ===== 第一步：验证身份 ===== -->
      <form
        v-if="step === 1"
        class="reset-form"
        @submit.prevent="handleNextStep"
      >
        <!-- 邮箱 -->
        <div class="form-group">
          <div class="input-icon">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                fill="currentColor"
              />
            </svg>
          </div>
          <input
            type="email"
            v-model="email"
            placeholder="请输入注册邮箱地址"
            class="form-input"
            autocomplete="email"
          />
        </div>

        <!-- 验证码 -->
        <div class="form-group captcha-group">
          <div class="input-icon">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
                fill="currentColor"
              />
            </svg>
          </div>
          <input
            type="text"
            v-model="captcha"
            placeholder="请输入验证码"
            class="form-input captcha-input"
            autocomplete="off"
          />
          <button
            type="button"
            class="captcha-btn"
            @click="sendCaptcha"
            :disabled="captchaSending"
          >
            {{ captchaText }}
          </button>
        </div>

        <!-- 下一步按钮 -->
        <button type="submit" class="next-btn">
          下一步
          <span class="btn-glow"></span>
        </button>
      </form>

      <!-- ===== 第二步：设置新密码 ===== -->
      <form v-else class="reset-form" @submit.prevent="handleResetPassword">
        <!-- 新密码 -->
        <div class="form-group">
          <div class="input-icon">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
                fill="currentColor"
              />
            </svg>
          </div>
          <input
            :type="showPassword ? 'text' : 'password'"
            v-model="newPassword"
            placeholder="请设置新密码（至少6位）"
            class="form-input"
            autocomplete="new-password"
          />
          <button type="button" class="password-toggle" @click="togglePassword">
            <svg v-if="showPassword" viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                fill="currentColor"
              />
            </svg>
            <svg v-else viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        <!-- 确认密码 -->
        <div class="form-group">
          <div class="input-icon">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
                fill="currentColor"
              />
            </svg>
          </div>
          <input
            :type="showConfirmPassword ? 'text' : 'password'"
            v-model="confirmNewPassword"
            placeholder="请再次输入新密码"
            class="form-input"
            autocomplete="new-password"
          />
          <button
            type="button"
            class="password-toggle"
            @click="toggleConfirmPassword"
          >
            <svg
              v-if="showConfirmPassword"
              viewBox="0 0 24 24"
              width="20"
              height="20"
            >
              <path
                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                fill="currentColor"
              />
            </svg>
            <svg v-else viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        <!-- 立即重置按钮 -->
        <button type="submit" class="reset-btn">
          立即重置
          <span class="btn-glow"></span>
        </button>
      </form>

      <!-- 底部 -->
      <div class="reset-footer">
        <span class="footer-line"></span>
        <p>还记得密码？</p>
        <button type="button" class="goto-login" @click="handleBack">
          去登录
          <span class="goto-arrow">→</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ResetPasswordComponent",
  data() {
    return {
      // 第一步：验证身份
      step: 1,
      email: "",
      captcha: "",
      captchaSending: false,
      captchaTimer: null,
      captchaCountdown: 0,

      // 第二步：设置密码
      newPassword: "",
      confirmNewPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      sendCodeIng: false,
    };
  },
  computed: {
    captchaText() {
      if (this.sendCodeIng) {
        return "发送中";
      }
      if (this.captchaSending) {
        return `${this.captchaCountdown}s`;
      }
      return "获取验证码";
    },
  },
  methods: {
    // ===== 发送验证码 =====
    async sendCaptcha() {
      // 验证邮箱
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.email || !emailRegex.test(this.email)) {
        this.$message.warning("请输入有效的邮箱地址");
        return;
      }

      if (this.sendCodeIng) {
        return;
      }
      this.sendCodeIng = true;

      console.log("发送验证码到:", this.email);
      // alert(`验证码已发送到 ${this.email}（演示功能）`);
      const res = await this.$api.code.sendCode({
        email: this.email,
        scene: "reset_password",
      });

      this.sendCodeIng = false;
      if (res.code == 1) {
        this.$message.warning(res.msg);
        return false;
      }

      this.$message.success("验证码发送成功");

      // 开始倒计时
      this.captchaSending = true;
      this.captchaCountdown = 60;

      this.captchaTimer = setInterval(() => {
        this.captchaCountdown--;
        if (this.captchaCountdown <= 0) {
          clearInterval(this.captchaTimer);
          this.captchaSending = false;
        }
      }, 1000);
    },

    // ===== 第一步 → 第二步 =====
    async handleNextStep() {
      // 验证邮箱
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.email || !emailRegex.test(this.email)) {
        this.$message.warning("请输入有效的邮箱地址");
        return;
      }

      // 验证验证码
      if (!this.captcha || this.captcha.length < 4) {
        this.$message.warning("请输入有效的验证码（至少4位）");
        return;
      }

      console.log("身份验证通过，进入第二步");

      const res = await this.$api.code.checkCode({
        email: this.email,
        code: this.captcha,
        scene: "reset_password",
      });

      if (res.code == 1) {
        this.$message.warning(res.msg);
        return false;
      }

      // this.$message.success("验证码验证通过");
      this.step = 2;
    },

    // ===== 第二步：重置密码 =====
    async handleResetPassword() {
      // 验证新密码
      if (!this.newPassword || this.newPassword.length < 6) {
        alert("新密码至少需要6位");
        return;
      }

      // 验证确认密码
      if (this.newPassword !== this.confirmNewPassword) {
        alert("两次输入的密码不一致");
        return;
      }

      console.log("密码重置成功:", {
        email: this.email,
        newPassword: this.newPassword,
      });

      const res = await this.$api.user.resetPwd({
        email: this.email,
        new_passwd: this.newPassword,
        confirm_passwd: this.confirmNewPassword,
        code: this.captcha,
      });

      if (res.code == 1) {
        this.$message.warning(res.msg);
        return false;
      }

      this.$message.success("密码重置成功！请重新登录");
      setTimeout(() => {
        this.$router.push("/login");
      }, 3000);
    },

    // ===== 切换密码显隐 =====
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    toggleConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    // ===== 返回登录 =====
    handleBack() {
      // 清理定时器
      if (this.captchaTimer) {
        clearInterval(this.captchaTimer);
        this.captchaTimer = null;
      }
      console.log("返回登录页");
      this.$router.push({ name: "Login" });
    },
  },
  beforeDestroy() {
    // 组件销毁前清理定时器
    if (this.captchaTimer) {
      clearInterval(this.captchaTimer);
      this.captchaTimer = null;
    }
  },
};
</script>

<style scoped>
/* ===== 容器 ===== */
.reset-container {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* ===== 背景图层 ===== */
.bg-layer {
  position: absolute;
  inset: 0;
  background-image: url("@/assets/imgs/dlbj.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transform: scale(1.05);
  z-index: 0;
}

/* ===== 重置卡片 ===== */
.reset-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  padding: 40px 40px 32px;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.reset-card:hover {
  border-color: rgba(100, 200, 255, 0.2);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6), 0 0 60px rgba(0, 150, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

/* ===== 返回按钮 ===== */
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  cursor: pointer;
  padding: 4px 0;
  transition: all 0.3s ease;
  font-family: inherit;
  margin-bottom: 16px;
}

.back-btn:hover {
  color: #00b4ff;
  transform: translateX(-4px);
}

/* ===== 头部 ===== */
.reset-header {
  text-align: center;
  margin-bottom: 32px;
}

.logo-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  margin-bottom: 12px;
  box-shadow: 0 8px 32px rgba(0, 100, 255, 0.3);
}

.title {
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 2px;
  margin: 0 0 4px;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
}

.subtitle {
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 1px;
  margin: 0 0 20px 0;
}

/* ===== 步骤指示器 ===== */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-top: 8px;
}

.step-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.06);
  border: 2px solid rgba(255, 255, 255, 0.1);
  transition: all 0.5s ease;
}

.step-dot.active {
  color: #fff;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  border-color: #00b4ff;
  box-shadow: 0 0 20px rgba(0, 100, 255, 0.3);
}

.step-dot.done {
  color: #fff;
  background: #00b4ff;
  border-color: #00b4ff;
}

.step-line {
  width: 48px;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.5s ease;
}

.step-line.active {
  background: linear-gradient(90deg, #00b4ff, #0066ff);
}

/* ===== 表单 ===== */
.reset-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  color: rgba(255, 255, 255, 0.35);
  z-index: 2;
  pointer-events: none;
  transition: color 0.3s;
}

.form-input {
  width: 100%;
  padding: 14px 16px 14px 48px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #fff;
  font-size: 15px;
  outline: none;
  transition: all 0.3s ease;
  font-family: inherit;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.form-input:focus {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(0, 150, 255, 0.5);
  box-shadow: 0 0 0 4px rgba(0, 100, 255, 0.1);
}

.form-group:focus-within .input-icon {
  color: #00b4ff;
}

.form-input:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px rgba(0, 50, 100, 0.3) inset !important;
  -webkit-text-fill-color: #fff !important;
  border-color: rgba(0, 150, 255, 0.3);
}

/* ===== 验证码 ===== */
.captcha-group {
  gap: 10px;
}

.captcha-input {
  padding-right: 120px;
}

.captcha-btn {
  position: absolute;
  right: 6px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  white-space: nowrap;
}

.captcha-btn:hover:not(:disabled) {
  background: rgba(0, 180, 255, 0.2);
  border-color: #00b4ff;
  color: #00b4ff;
}

.captcha-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== 密码切换 ===== */
.password-toggle {
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
}

.password-toggle:hover {
  color: rgba(255, 255, 255, 0.7);
}

/* ===== 下一步 / 立即重置 按钮 ===== */
.next-btn,
.reset-btn {
  position: relative;
  padding: 15px;
  margin-top: 8px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  border: none;
  border-radius: 12px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  letter-spacing: 2px;
  font-family: inherit;
}

.next-btn:hover,
.reset-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 100, 255, 0.35);
}

.next-btn:active,
.reset-btn:active {
  transform: translateY(0);
  box-shadow: 0 4px 20px rgba(0, 100, 255, 0.2);
}

.btn-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.15) 0%,
    transparent 60%
  );
  opacity: 0;
  transition: opacity 0.6s;
  pointer-events: none;
}

.next-btn:hover .btn-glow,
.reset-btn:hover .btn-glow {
  opacity: 1;
}

/* ===== 底部 ===== */
.reset-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.footer-line {
  display: block;
  width: 40px;
  height: 2px;
  margin: 0 auto 12px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.15),
    transparent
  );
  border-radius: 2px;
}

.reset-footer p {
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  margin: 0;
}

.goto-login {
  background: transparent;
  border: none;
  color: #00b4ff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
  font-family: inherit;
  position: relative;
}

.goto-login::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #00b4ff, #0066ff);
  transition: width 0.3s ease;
}

.goto-login:hover {
  color: #66ccff;
  transform: translateX(4px);
}

.goto-login:hover::after {
  width: 100%;
}

.goto-arrow {
  display: inline-block;
  transition: transform 0.3s ease;
}

.goto-login:hover .goto-arrow {
  transform: translateX(4px);
}

/* ===== 响应式适配 ===== */
@media (max-width: 480px) {
  .reset-card {
    padding: 28px 20px 24px;
    border-radius: 16px;
    margin: 16px;
  }

  .title {
    font-size: 22px;
  }

  .subtitle {
    font-size: 12px;
  }

  .form-input {
    padding: 12px 12px 12px 42px;
    font-size: 14px;
  }

  .captcha-input {
    padding-right: 100px;
  }

  .captcha-btn {
    font-size: 12px;
    padding: 6px 12px;
  }

  .next-btn,
  .reset-btn {
    padding: 13px;
    font-size: 15px;
  }

  .bg-layer {
    filter: blur(8px) brightness(0.6);
    -webkit-filter: blur(8px) brightness(0.6);
  }

  .reset-footer {
    flex-direction: column;
    gap: 4px;
  }

  .step-line {
    width: 30px;
  }

  .step-dot {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
}
</style>
